/**
 * weapon_voices.ts — which clips a weapon can make (2026-09-20). PURE.
 *
 * The first shot of a freshly equipped gun used to be SILENT: a clip was only
 * fetched and decoded when it was first asked to play, and a one-shot that is
 * not decoded by then is gone (it may arrive late, but only inside a 180 ms
 * deadline — a cold fetch + decode of a gun voice is longer). The combat HUD
 * warms every weapon in the ring instead: the ring is the set of things the
 * next wheel notch can put in your hand, so it is exactly the set whose first
 * trigger must not miss. Small by construction (a bag holds a handful).
 *
 * Kept OUT of weapon_catalog.ts on purpose — that file is a byte-mirrored
 * client/server contract and the server has no use for clip names.
 */

export interface VoicedWeapon {
  key: string;
  kind: 'gun' | 'melee';
  feed?: { kind: 'magazine' | 'energy' } | undefined;
}

/** The generic clips every armed player can trigger (bare hands, a dry click, the two impact families, the swap). */
export const COMMON_COMBAT_KEYS: readonly string[] = [
  'sfx_weapon_swing', 'sfx_dry_fire', 'sfx_equip', 'sfx_impact_slug', 'sfx_impact_energy',
];

/** Every clip ONE weapon can make: its own voice, and its feed's (a magazine reloads, a capacitor recharges). */
export function weaponVoiceKeys(w: VoicedWeapon): string[] {
  if (w.key === '') return ['sfx_weapon_swing'];
  if (w.kind === 'melee') return [`sfx_melee_${w.key}`];
  const keys = [`sfx_gun_${w.key}`];
  if (w.feed?.kind === 'magazine') keys.push('sfx_reload_magazine');
  else if (w.feed?.kind === 'energy') keys.push('sfx_recharge_capacitor');
  return keys;
}

/** The warm set for a whole ring, de-duplicated, in a stable order (so its signature is comparable tick to tick). */
export function ringVoiceKeys(ring: readonly VoicedWeapon[]): string[] {
  const out = new Set<string>(COMMON_COMBAT_KEYS);
  for (const w of ring) for (const k of weaponVoiceKeys(w)) out.add(k);
  return [...out].sort();
}
